/**
 * English - `team`: the Team members screen, its dialogs, and the shared
 * permission editor.
 *
 * Keys: `page.*` for the screen's header and banners; `filters.*`, `table.*`,
 * `empty.*` and `actions.*` for the list; `member.*` for what a row says about
 * one person; `blocked.*` for why a row cannot be edited; `status.<CODE>` and
 * `roles.<CODE>` keyed by the API's enum values; `fields.*` for form labels
 * shared by the invite and edit dialogs; `invite.*`, `edit.*` and
 * `set_password.*` for each dialog; `password.*` for the password chooser and
 * its policy; `seats.*` for the seat usage card; `activity.*` for the activity
 * trail (`activity.actions.<ACTION>` keyed by the audit action code, with
 * `{actor}` and `{target}` rendered as names); `permissions.*` for
 * `components/PermissionEditor`. Role, module and permission names come from
 * the API's permission catalogue and are not translated here.
 */
const team: Record<string, string> = {
  'page.title': 'Team members',
  'page.subtitle': 'Staff accounts for your business, and what each of them can do.',
  'page.invite_member': 'Invite member',
  'page.no_seats_left': 'Your plan has no seats left.',
  'page.load_failed': 'Could not load your team.',
  'page.invitation_resent': 'Invitation sent again.',
  'errors.generic': 'Something went wrong. Try again.',

  'filters.search_placeholder': 'Name or email',
  'filters.all_roles': 'All roles',
  'filters.all_statuses': 'All statuses',

  'status.ACTIVE': 'Active',
  'status.INVITED': 'Invited',
  'status.DISABLED': 'Deactivated',
  'roles.TENANT_OWNER': 'Owner',
  'roles.TENANT_ADMIN': 'Admin',
  'roles.MANAGER': 'Manager',
  'roles.STAFF': 'Staff',

  'table.person': 'Person',
  'table.role': 'Role',
  'table.status': 'Status',
  'table.last_signed_in': 'Last signed in',

  'empty.no_matches_title': 'No matching members',
  'empty.no_members_title': 'No team members yet',
  'empty.no_members_description': 'Invite a colleague and choose what they can see and do.',

  'actions.resend': 'Resend',
  'actions.set_password': 'Set password',
  'actions.reactivate': 'Reactivate',
  'actions.deactivate': 'Deactivate',

  'member.you': '(you)',
  'member.primary_owner': 'Primary owner',
  'member.custom': 'Custom',
  'member.temporary_password': 'Temporary password',

  'blocked.primary_owner': 'The primary owner cannot be changed.',
  'blocked.self': 'You cannot change your own access.',
  'blocked.owner_only': 'Only an owner can change another owner.',

  'fields.full_name': 'Full name',
  'fields.phone': 'Phone',
  'fields.email': 'Email',
  'fields.work_email': 'Work email',
  'fields.role': 'Role',
  'fields.status': 'Status',
  'fields.access': 'Access',

  'invite.title': 'Invite a team member',
  'invite.phone_hint': 'Optional.',
  'invite.create_account': 'Create account',
  'invite.send_invitation': 'Send invitation',
  'invite.email_hint_invite': 'They set their own password through the emailed link.',
  'invite.email_hint_password': 'Used to sign in. We tell them the account exists, but never email the password.',
  'invite.sign_in': 'Sign-in',
  'invite.credentials_legend': 'How they get their password',
  'invite.credentials_invite_label': 'Email them an invitation link',
  'invite.credentials_invite_description':
    'They choose their own password, so nobody else ever knows it. Prefer this whenever they have an address of their own.',
  'invite.credentials_password_label': 'Set a password now',
  'invite.credentials_password_description':
    'For someone you will pass the password to directly. They are asked to change it the first time they sign in.',

  'edit.title': 'Edit {name}',
  'edit.email_locked': 'Email cannot be changed.',
  'edit.owner_note': 'This member is an owner. Change their role to narrow what they can do.',

  'set_password.title': 'Set a password for {name}',
  'set_password.warning_invited':
    'This will replace their unused invitation. Use it when they have no working email address — otherwise resend the invitation and let them choose their own password.',
  'set_password.warning_active':
    'They will be signed out on every device, and you will know their password until they change it. Prefer sending a reset link if they can receive one.',

  'password.label': 'Password',
  'password.hint': 'At least 10 characters. Share it in person or over a channel you trust.',
  'password.generate': 'Generate',
  'password.generate_title': 'Generate a password',
  'password.hide': 'Hide password',
  'password.show': 'Show password',
  'password.copy': 'Copy password',
  'password.note':
    'You will be able to see this password until you close this dialog, and not afterwards — it is stored only as a hash. Your colleague is asked to choose their own the first time they sign in, so this one stops working then.',
  'password.problems.too_short': 'Use at least 10 characters.',
  'password.problems.too_long': 'Use at most 128 characters.',
  'password.problems.only_numbers': 'Use something other than only numbers.',
  'password.problems.too_few_distinct': 'Use at least 5 different characters.',

  'seats.used_of_limit': '{used} of {limit} seats used',
  'seats.used_of_unlimited': '{used} of unlimited seats used',
  'seats.plan_note': "On the {plan} plan. The owner's account counts as a seat; a deactivated member does not.",
  'seats.upgrade_to': 'Upgrade to {plan}',
  'seats.see_plans': 'See plans',
  'seats.progress_label': 'Seats used',
  'seats.at_limit': 'You have used every seat your plan allows. Deactivate a member to free one, or upgrade for more.',

  'activity.title': 'Recent team activity',
  'activity.load_failed': 'Could not load the activity trail.',
  'activity.empty': 'Nothing yet. Invitations, role changes and permission changes appear here.',
  'activity.removed_account': 'A removed account',
  'activity.a_colleague': 'a colleague',
  'activity.via_support': 'via support',
  'activity.actions.USER_INVITED': '{actor} invited {target}',
  'activity.actions.USER_INVITE_RESENT': '{actor} resent an invitation to {target}',
  'activity.actions.USER_ROLE_CHANGED': '{actor} changed the role of {target}',
  'activity.actions.USER_PERMISSIONS_CHANGED': '{actor} changed permissions for {target}',
  'activity.actions.USER_UPDATED': '{actor} updated the details of {target}',
  'activity.actions.USER_DISABLED': '{actor} deactivated {target}',
  'activity.actions.USER_REENABLED': '{actor} reactivated {target}',
  'activity.actions.other': '{actor} {action} {target}',

  'permissions.owner_note':
    'An owner has full access to everything. Permissions cannot be narrowed for an owner — change their role first.',
  'permissions.owner_only': 'Owner only',
  'permissions.customise_label': "Customise this member's permissions",
  'permissions.customise': 'Customise permissions',
  'permissions.customised_note': 'This member keeps exactly the permissions ticked below, even if the role changes later.',
  'permissions.following_role': 'Following the {role} role. Tick this to choose permissions one at a time.',
  'permissions.everyone': 'Everyone',
};

export default team;
